/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {GenericObject} from '@ion/core/src/types/entities';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {CTALink} from '../buttons/CTALink';

/**
 * Defines the prop type definitions for the LinkCell component, which are
 * as follows:
 *
 * data: GenericObject: The row data that selected.
 * onClick?: Function: The click event handler for the cell.
 * fontSize?: string | number: Cell font size.
 * textDecoration?: string: Cell font decoration.
 * color?: string: Cell font color.
 */
export interface LinkCellProps {
  data: GenericObject;
  value?: string;
  onClick?: Function;
  fontSize?: string | number;
  textTransform?: string;
  textDecoration?: string;
  color?: string;
};

// defines the styles used by LinkCell component
const useStyles = makeStyles(theme => {
  return {
    ctaLink : {
      ...dlsTypography('cta'),
      color          : ({color}: LinkCellProps) => color || dlsColor('grey1'),
      cursor         : 'pointer',
      letterSpacing  : '1px',
      lineHeight     : 'inherit',
      fontSize       : ({fontSize}: LinkCellProps) => fontSize || 'inherit',
      textTransform  : ({textTransform}: LinkCellProps) => textTransform || 'uppercase',
      textDecoration : ({textDecoration}: LinkCellProps) => textDecoration || 'underline'
    }
  };
});

/**
 * The LinkCell component provides the option to place the cta button cell in the grid.
 * This basically provides the CTA link to the component where the wrapping component can
 * change some text style such as text color, size, decoration of this component. This is basically
 * very useful on column click with cell. This can usefu;l for redirect to some page
 * based on cell value, change any page view based on cell value, etc.
 *
 * ```typescript
 *
 *  import {LinkCell} from '@ion/dls/src/components/grid/LinkCell';
 *
 *  <LinkCell data={data}
 *    onClick={onClick} /> // [Basic]
 *
 *  <LinkCell data={data}
 *    fontSize={16} // or '16px'
 *    textDecoration={'none'} // default would be underline
 *    color={dlsColor('caBlue')} // default would be grey1
 *    onClick={onClick} /> // [Basic + text style change]
 *
 * ```
 */
export const LinkCell = ({
  data,
  value,
  onClick,
  fontSize,
  textTransform,
  textDecoration,
  color
}: LinkCellProps) => {
  const classes = useStyles({
    fontSize,
    textTransform,
    textDecoration,
    color
  });
  return (
    <CTALink className={classes.ctaLink}
      onClick={onClick ? function(evt) {
        evt.stopPropagation();
        evt.preventDefault();
        onClick?.(value ?? data);
      } : null}>
      {value ?? data?.getValue()}
    </ CTALink>
  );
};

export default LinkCell;
